/* ============================================================================
 * Project        : Deloitte Digital Compliance
 * File           : MindMapPage.jsx
 * Version        : 1.0.0
 * Author         : Team Neubinary
 * Created On     : 2026-06-24
 *
 * Description    : Digital Compliance for Automation and Governance
 *
 * Change History :
 * ----------------------------------------------------------------------------
 * Date         | Version | Author           | Description
 * ----------------------------------------------------------------------------
 * 2026-06-24   | 1.0.0   | Team Neubinary   | Initial creation
 * <YYYY-MM-DD> | x.x.x   | <Name/Team>      | <Change description>
 * ============================================================================
 *
 * Copyright (c) 2026 AU79 Consulting Services Pvt. Ltd.
 * All Rights Reserved.
 *
 * This source code and its contents are confidential and proprietary to
 * AU79 Consulting Services Pvt. Ltd. Unauthorized copying, reproduction,
 * modification, distribution, or disclosure of this material, in whole or
 * in part, is strictly prohibited without prior written permission from
 * AU79 Consulting Services Pvt. Ltd.
 * ============================================================================
 */
/**
 * @author Tahir Shaik - tahir@au79consulting.com
 * @date 12-23-2024
 * @description This is the dedicated Mind Map Page for viewing mind maps
 */

import React from "react";
import { Box } from "@mui/material";
import { useSelector } from "react-redux";
import VlcMindMap from "./MindMap";
import VlcZonalMindMap from "./ZonalMindMap";
import CommonBreadcrumbs from "../../components/customComponentsV2/common/Breadcrumbs/CommonBreadcrumbs";
import ProjectVariantSearchbar from "../../components/customComponentsV2/common/projectSearchbar/ProjectvariantSearchbar";
import ProjectVariantDialog from "../../components/customComponentsV2/vlc/ProjectVarientAccordian/ProjectVariantDialog";

function MindMapPage() {
  const selectedProjectDetails = useSelector(
    (state) => state?.projectConfigReducer?.selectedProjectDetails
  );
  const selectedSingleVariantDetails = useSelector(
    (state) => state?.projectConfigReducer?.selectedVariantDetails
  );

  const canRenderMap =
    selectedProjectDetails && selectedSingleVariantDetails?.variant_id;

  const breadcrumbItems = [{ label: "Virtual Lab-Car" }, { label: "Mind Map" }];

  return (
    <Box
      sx={{
        margin: 0,
        padding: 0,
        width: "100%",
        height: "100%",
        overflow: "auto",
        display: "flex",
        flexDirection: "column",
        backgroundColor: "#F5F5F5",
      }}
    >
      {/* Breadcrumbs */}
      <Box
        sx={{
          padding: { xs: "8px 8px 0px 8px", md: "12px 12px 0px 12px" },
          flexShrink: 0,
        }}
      >
        <CommonBreadcrumbs items={breadcrumbItems} />
      </Box>

      {/* Project Selection and Details Bar */}
      <Box
        sx={{
          display: "flex",
          alignItems: "center",
          justifyContent: "space-between",
          padding: { xs: "12px 16px", md: "12px 24px" },
          flexShrink: 0,
          gap: 2,
          flexWrap: "wrap",
        }}
      >
        {/* Project Selection Dropdowns */}
        <Box
          sx={{
            display: "flex",
            alignItems: "center",
            gap: 1.5,
            flexShrink: 0,
          }}
        >
          <ProjectVariantSearchbar />
          <ProjectVariantDialog />
        </Box>
      </Box>

      {/* Mind Map Component */}
      <Box
        sx={{
          flex: 1,
          overflow: "auto",
          padding: { xs: "8px", md: "12px" },
        }}
      >
        {canRenderMap &&
          (selectedProjectDetails?.is_zonal ? (
            <VlcZonalMindMap
              selectedProjectId={selectedProjectDetails?.project_id}
              selectedVariantId={selectedSingleVariantDetails?.variant_id}
              isDialog={true}
            />
          ) : (
            <VlcMindMap
              selectedProjectId={selectedProjectDetails?.project_id}
              selectedVariantId={selectedSingleVariantDetails?.variant_id}
            />
          ))}
      </Box>
    </Box>
  );
}

export default MindMapPage;
